/**
 * Fontes de dados trocáveis (§14.8.1; FE §3.12, DE-B09). Só tipos e o erro comum: a fábrica `createSource` mora em
 * `app/createSource.ts` (o `import()` de `src/sim/` é assíncrono, por isso devolve `Promise`). Toda fonte entrega o
 * BRUTO (`unknown`): a validação é do `FeedClient`, igual para todas (§14.7).
 */
import type { AppConfig } from '../config';
import type { IbgeCode, Office, PlaceCode } from '../contracts/base';
import type { Baseline2022, CandidateList, FirstRoundResults, Geography } from '../contracts/static';

export interface SourceResponse<T> {
  /** null = sem mudança (ETag igual ao último processado). */
  data: T | null;
  etag: string | null;
  /** Na simulação: "sim:base:2026@18:30:55". */
  url: string;
  /** Relógio da fonte no recebimento (ms). */
  receivedAtMs: number;
  /** Cabeçalho HTTP `Date` (exposto, §14.10) → ValidationContext.serverNowMs (I-04); null fora do http. */
  serverDateMs: number | null;
}

export type SourceErrorKind = 'network' | 'timeout' | 'http' | 'size' | 'parse' | 'injected';

export class SourceError extends Error {
  constructor(
    public kind: SourceErrorKind,
    public status?: number,
    public retryAfterMs?: number,
  ) {
    super(status === undefined ? kind : `${kind} ${status}`);
    this.name = 'SourceError';
  }
}

export interface FetchOptions {
  signal: AbortSignal;
  etag?: string | null;
}

/** Estáticos (§2.2.4): só `candidates` bloqueia; os demais são promessas (preguiçosas no HttpSource, DE-B10). */
export interface Statics {
  candidates: CandidateList;
  readonly geography: Promise<Geography>;
  /** null se ausente ou inválido. */
  readonly firstRound: Promise<FirstRoundResults | null>;
  /** null se ausente ou inválido. */
  readonly baseline: Promise<Baseline2022 | null>;
}

/** Estado do relógio virtual (simulação e replay gravado). */
export interface TimeControlState {
  playing: boolean;
  speed: number;
  nowTMin: number;
  startTMin: number;
  endTMin: number;
}

/** Falha injetável pelo DevPanel (a forma exata é `SimFailure`, de src/sim/params.ts, que data/ não importa). */
export interface InjectableFailure {
  type: string;
}

export interface TimeControl {
  play(): void;
  pause(): void;
  setSpeed(x: 1 | 5 | 15 | 60 | 300): void;
  seek(tMin: number): void;
  state(): TimeControlState;
  /** DevPanel. */
  injectFailure?(f: InjectableFailure): void;
  /** DevPanel (OracleSimEngine). */
  approveCall?(): void;
}

/** Instante pronto para o replay síncrono (simulação: `simulate(params).at(isoAt(tMin))`, §14.8.7 passo 2). */
export interface InstantFiles {
  snapshot: unknown;
  municipal: unknown | null;
}

export type PublishReason = 'publish' | 'control';

export interface DataSource {
  /** "http:feed.mapa.voto" · "sim:base:2026" · "fixture:base@pct30". */
  readonly id: string;
  readonly kind: 'http' | 'simulation' | 'replay' | 'fixture';
  /**
   * Relógio da fonte em ms (epoch). Real no http; virtual na simulação e no replay. Fase, manchete, contagem
   * regressiva, "há N s" e cortes por horário usam SEMPRE este relógio.
   */
  clock(): number;
  /**
   * Só no http: com a noite gravada (`origin: replay`, servida por HTTP no ensaio; P-16, P-18), o "agora" é a
   * hora da noite gravada. O FeedClient passa a hora do snapshot aceito e o relógio passa a segui-la (só para a frente),
   * deixando de seguir o `Date` do servidor, que é o dia do ensaio. A produção só aceita `tse` (E_ORIGIN): nunca chama.
   */
  followFeedClock?(feedNowMs: number): void;
  /**
   * Só no http: a pré-busca do boot (§15.7.4) não passa pela fonte; o FeedClient entrega o `Date` e o `Age` dela, e a
   * fonte corrige o relógio como faria numa resposta própria, antes de o 1º snapshot ser aceito (pré-mortem de 25/10,
   * ORD-F-148 M4: sem isso, o 1º "agora" era o do aparelho e a 2ª resposta o corrigia de uma vez).
   */
  learnServerDate?(dateHeader: string | null, age: string | null, localNowMs: number): void;
  statics(): Promise<Statics>;
  snapshot(o: FetchOptions): Promise<SourceResponse<unknown>>;
  municipal(path: string, o: FetchOptions): Promise<SourceResponse<unknown>>;
  stateDetail(seq: number, place: PlaceCode, office: Office, o: FetchOptions): Promise<SourceResponse<unknown>>;
  /** P2. */
  zones?(ibge: IbgeCode, o: FetchOptions): Promise<SourceResponse<unknown>>;
  history(o: FetchOptions): Promise<SourceResponse<unknown>>;
  events(o: FetchOptions): Promise<SourceResponse<unknown>>;
  probabilityDetail(path: string, o: FetchOptions): Promise<SourceResponse<unknown>>;
  /**
   * Situação incomum (P-27): o índice (`files.insights` do snapshot) e o pacote de cada sinal, pelo caminho relativo
   * ao feed. Ausente = a fonte não tem a camada (a simulação).
   */
  insights?(path: string, o: FetchOptions): Promise<SourceResponse<unknown>>;
  /** Comentários ao vivo (P-32): o índice (`files.commentary` do snapshot). Ausente = a fonte não tem a camada. */
  commentary?(path: string, o: FetchOptions): Promise<SourceResponse<unknown>>;
  archive?: {
    index(o: FetchOptions): Promise<SourceResponse<unknown>>;
    snapshotAt(path: string, o: FetchOptions): Promise<SourceResponse<unknown>>;
  };
  /**
   * Push na simulação e no replay com velocidade > 5 (`'publish'`); também avisa mudanças do controle (`'control'`:
   * play, pausa, seek, velocidade, falha injetada), para o cliente buscar na hora.
   */
  subscribe?(onPublish: (reason?: PublishReason) => void): () => void;
  /** Só simulação e replay. */
  control?: TimeControl;
  /**
   * Extensão (FE §8.4): instante síncrono para `FeedClient.at()` quando a fonte é uma função pura do tempo
   * (simulação e fixture). Sem ele, o replay passa pelo `archive/index.json`.
   */
  instantAt?(tMin: number): InstantFiles | null;
  /** Failover (§14.8.2): número de origens e troca da atual (só o HttpSource). */
  originCount?(): number;
  setOrigin?(i: number): void;
}

/** Assinatura da fábrica (implementação em app/createSource.ts, DE-B09). */
export type CreateSource = (config: AppConfig, query: URLSearchParams) => Promise<DataSource>;

/** Resposta montada por fontes sem HTTP (simulação, fixture). */
export function localResponse<T>(data: T | null, url: string, receivedAtMs: number, etag: string | null = null) {
  const res: SourceResponse<T> = { data, etag, url, receivedAtMs, serverDateMs: null };
  return res;
}

/** Erro de "não existe" das fontes locais (como um 404 do HTTP). */
export const notFound = () => new SourceError('http', 404);

/** Rejeita se o sinal já foi abortado (fontes locais, que não fazem fetch). */
export function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    const err = new Error('aborted');
    err.name = 'AbortError';
    throw err;
  }
}
